import { Button, Container, toast } from "@medusajs/ui"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { EmptyState, ErrorState, PageHeader, SearchInput } from "@/components/ui"
import { apiFetch, errorMessage } from "@/lib/sdk"
import { Badge, FilterBar, FilterSelect, LoadingCard } from "@/terabizz"

/**
 * Settings → Messages (backend lib/message-log.ts): every email and text message the store sent,
 * whichever provider sent it, with what happened to it (delivered, bounced… from SendGrid's
 * delivery webhook). Never the content: emails carry sign-in, reset and card-update links.
 * Failed order and card emails can be resent (POST /admin/terify/messages/:id/resend rebuilds them
 * from the kind + order / customer).
 */

type Status = "logged" | "sent" | "failed" | "delivered" | "deferred" | "bounced" | "dropped" | "spam_complaint"
type Message = { id: string; channel: "email" | "sms"; kind: string | null; recipient: string; subject: string | null; provider: string; provider_id: string | null; status: Status; error: string | null; events: { type: string; at: string; reason?: string; link?: number }[]; created_at: string; updated_at: string; links?: { n: number; path: string }[]; click_count?: number; first_clicked_at?: string | null; customer_id?: string | null; ref?: string | null }
type Resp = { messages: Message[]; count: number; last_7_days: Partial<Record<Status, number>>; providers: { email: string; sms: string | null; delivery_updates: boolean } }

const PAGE = 50
const PROVIDER: Record<string, string> = { sendgrid: "Twilio SendGrid", postmark: "Postmark", ses: "Amazon SES", log: "Not set up (server log only)", twilio_verify: "Twilio Verify", twilio: "Twilio Messaging", twilio_messaging: "Twilio Messaging (customer texts) and Verify (sign-in codes)", klaviyo: "Klaviyo", none: "Not handed to a provider (failed before sending)" }
const STATUS: Record<Status, { label: string; tone: "neutral" | "success" | "attention" | "critical" | "info" }> = {
  logged: { label: "Not sent (email not set up)", tone: "attention" },
  sent: { label: "Sent", tone: "info" },
  delivered: { label: "Delivered", tone: "success" },
  deferred: { label: "Delayed", tone: "attention" },
  bounced: { label: "Bounced", tone: "critical" },
  dropped: { label: "Not delivered", tone: "critical" },
  spam_complaint: { label: "Marked as spam", tone: "critical" },
  failed: { label: "Failed", tone: "critical" },
}
const KIND: Record<string, string> = {
  order_confirmation: "Order confirmation",
  shipping_confirmation: "Shipping confirmation",
  abandoned_checkout: "Abandoned checkout",
  invoice: "Invoice",
  contact_form: "Contact form",
  cs_reply: "Reply to a customer message",
  customer_sign_in_code: "Customer sign-in code",
  password_reset: "Password reset",
  staff_invite: "Staff invitation",
  ownership_transfer: "Ownership transfer",
  security_alert: "Security alert",
  subscriptions_digest: "Subscriptions digest",
  sign_in_code: "Staff sign-in code",
}
/** Kinds the backend can rebuild (lib/notifications.ts ORDER_RESEND_KINDS, and sub_card_* through Recovery). */
const RESENDABLE = new Set(["order_confirmation", "shipping_confirmation", "order_canceled", "refund_notification", "return_requested", "return_received", "staff_new_order"])
const canResend = (m: Message) => m.channel === "email" && !!m.kind && ((RESENDABLE.has(m.kind) && !!m.ref?.startsWith("order_")) || (m.kind.startsWith("sub_card_") && !!m.customer_id))
const NOT_REACHED: Status[] = ["failed", "bounced", "dropped"]
/** Where a message's ref points in the admin (orders; anything else is shown as text). */
const refLink = (ref: string) => (ref.startsWith("order_") ? `/orders/${ref}` : ref.startsWith("cmsg_") ? `/messages/${ref}` : null)
const kindLabel = (k: string | null) => (k ? KIND[k] ?? k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Other")
const when = (s: string) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })

export function MessagesPage() {
  const [params, setParams] = useSearchParams()
  const get = (k: string) => params.get(k) ?? ""
  const page = Math.max(0, Number(params.get("page") ?? 1) - 1)
  const query = { limit: String(PAGE), offset: String(page * PAGE), ...(get("q") ? { q: get("q") } : {}), ...(get("channel") ? { channel: get("channel") } : {}), ...(get("status") ? { status: get("status") } : {}) }
  const qs = new URLSearchParams(query).toString()
  const { data, isLoading, isFetching, error } = useQuery({ queryKey: ["messages", qs], queryFn: () => apiFetch<Resp>(`/admin/terify/messages?${qs}`), placeholderData: keepPreviousData, refetchInterval: 30_000 })
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    if (!("page" in patch)) next.delete("page")
    setParams(next, { replace: true })
  }
  const filtered = !!(get("q") || get("channel") || get("status"))
  const count = data?.count ?? 0
  const s = data?.last_7_days ?? {}
  const sent = (s.sent ?? 0) + (s.delivered ?? 0) + (s.deferred ?? 0) + (s.bounced ?? 0) + (s.dropped ?? 0) + (s.spam_complaint ?? 0)
  const problems = (s.failed ?? 0) + (s.bounced ?? 0) + (s.dropped ?? 0) + (s.spam_complaint ?? 0)

  return (
    <>
      <PageHeader title="Messages" subtitle="Every email and text message the store sends, and what happened to it." />
      {data ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ["Sent · 7 days", sent, {}],
              ["Delivered · 7 days", s.delivered ?? 0, { status: "delivered" }],
              ["Problems · 7 days", problems, { status: s.failed ? "failed" : "bounced" }],
              ["Not sent · 7 days", s.logged ?? 0, { status: "logged" }],
            ] as [string, number, Record<string, string>][]
          ).map(([label, n, f]) => (
            <button key={label} type="button" onClick={() => set({ q: null, channel: null, status: null, ...f })} className="tf-card rounded-[10px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)] px-3 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)]">
              <span className="block text-[12px] text-[var(--tb-ink-3)]">{label}</span>
              <span className="block text-[20px] font-semibold tabular-nums text-[var(--tb-ink)]">{n.toLocaleString()}</span>
            </button>
          ))}
        </div>
      ) : null}
      {data ? (
        <p className="mb-3 text-[13px] text-[var(--tb-ink-3)]">
          Email: <span className="text-[var(--tb-ink)]">{PROVIDER[data.providers.email] ?? data.providers.email}</span>
          {" · "}Text messages: <span className="text-[var(--tb-ink)]">{data.providers.sms ? PROVIDER[data.providers.sms] : "not set up"}</span>
          {data.providers.email === "sendgrid" && !data.providers.delivery_updates ? " · Delivery updates aren't connected yet (SendGrid event webhook)." : ""}
        </p>
      ) : null}
      <Container className="tf-card overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 [&>div]:max-w-none">
          <SearchInput value={get("q")} onChange={(v) => set({ q: v || null })} placeholder="Search by email, subject, order id or customer id" />
        </div>
        <FilterBar>
          <FilterSelect label="Type" value={get("channel")} onChange={(v) => set({ channel: v || null })} options={[["email", "Email"], ["sms", "Text message"]]} />
          <FilterSelect label="Status" value={get("status")} onChange={(v) => set({ status: v || null })} options={(Object.keys(STATUS) as Status[]).map((k) => [k, STATUS[k].label])} />
          {filtered && (
            <button type="button" className="txt-compact-small ml-auto text-ui-fg-interactive hover:underline" onClick={() => set({ q: null, channel: null, status: null })}>
              Clear filters
            </button>
          )}
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : isLoading ? (
          <div className="p-4"><LoadingCard lines={6} /></div>
        ) : !data?.messages.length ? (
          <EmptyState title="No messages found" body={filtered ? "Try clearing the filters." : "Messages appear here as the store sends them."} />
        ) : (
          <ul className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={isFetching}>
            {data.messages.map((m) => <Row key={m.id} m={m} onFilter={set} />)}
          </ul>
        )}
        {count > 0 && (
          <div className="flex items-center justify-between border-t border-[var(--tb-line)] px-4 py-2.5">
            <span className="txt-compact-small tabular-nums text-[var(--tb-ink-3)]">{page * PAGE + 1}–{Math.min(count, (page + 1) * PAGE)} of {count.toLocaleString()}</span>
            <div className="flex gap-x-2">
              <Button size="small" variant="secondary" disabled={page === 0} onClick={() => set({ page: page > 1 ? String(page) : null })}>Previous</Button>
              <Button size="small" variant="secondary" disabled={(page + 1) * PAGE >= count} onClick={() => set({ page: String(page + 2) })}>Next</Button>
            </div>
          </div>
        )}
      </Container>
    </>
  )
}

function Row({ m, onFilter }: { m: Message; onFilter: (p: Record<string, string | null>) => void }) {
  const [open, setOpen] = useState(false)
  const qc = useQueryClient()
  const resend = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; sent: number; to?: string[]; reason?: string }>(`/admin/terify/messages/${m.id}/resend`, { method: "POST", body: "{}" }),
    onSuccess: (r) => {
      toast.success(r.sent ? `Sent again${r.to?.length ? ` to ${r.to.join(", ")}` : ""}` : `Not sent${r.reason ? `: ${r.reason}` : ""}`)
      qc.invalidateQueries({ queryKey: ["messages"] })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const st = STATUS[m.status] ?? { label: m.status, tone: "neutral" as const }
  const failed = NOT_REACHED.includes(m.status)
  const href = m.ref ? refLink(m.ref) : null
  return (
    <li className="border-t border-[var(--tb-line)] first:border-t-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="grid w-full grid-cols-[1fr_auto] items-start gap-x-3 px-4 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)] sm:grid-cols-[130px_1fr_auto]">
        <span className="hidden text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:block">{when(m.created_at)}</span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] text-[var(--tb-ink)]">{m.subject ?? kindLabel(m.kind)}</span>
          <span className="block truncate text-[12px] text-[var(--tb-ink-3)]">
            {m.channel === "sms" ? "Text · " : ""}{m.recipient} · {kindLabel(m.kind)}{m.ref ? <span className="font-mono"> · {m.ref}</span> : null}
          </span>
          <span className="block text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:hidden">{when(m.created_at)}</span>
        </span>
        <span className="flex flex-col items-end gap-1 pt-0.5">
          <Badge tone={st.tone}>{st.label}</Badge>
          {failed && canResend(m) ? <span className="text-[11px] text-ui-fg-interactive">Can be resent</span> : null}
          {m.click_count ? <span className="text-[11px] text-[var(--tb-ink-3)]">{m.click_count} click{m.click_count === 1 ? "" : "s"}</span> : null}
        </span>
      </button>
      {open && (
        <div className="mx-4 mb-3 rounded-[8px] bg-[var(--tb-canvas)] px-3 py-2.5 text-[12px] sm:ml-[146px]">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-[var(--tb-ink-3)]">To</dt>
            <dd className="break-all">
              {m.channel === "email" ? <button type="button" className="text-ui-fg-interactive hover:underline" onClick={() => onFilter({ q: m.recipient })}>{m.recipient}</button> : m.recipient}
              {m.channel === "email" ? <span className="text-[var(--tb-ink-3)]"> (show all messages to this address)</span> : null}
            </dd>
            {m.ref ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">{m.ref.startsWith("order_") ? "Order" : "About"}</dt>
                <dd className="break-all">
                  {href ? <Link to={href} className="font-mono text-ui-fg-interactive hover:underline">{m.ref}</Link> : <span className="font-mono">{m.ref}</span>}
                  <button type="button" className="ml-2 text-ui-fg-interactive hover:underline" onClick={() => onFilter({ q: m.ref! })}>all messages</button>
                </dd>
              </>
            ) : null}
            {m.customer_id ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Customer</dt>
                <dd className="break-all">
                  <Link to={`/customers/${m.customer_id}`} className="font-mono text-ui-fg-interactive hover:underline">{m.customer_id}</Link>
                  <button type="button" className="ml-2 text-ui-fg-interactive hover:underline" onClick={() => onFilter({ q: m.customer_id! })}>all messages</button>
                </dd>
              </>
            ) : null}
            <dt className="text-[var(--tb-ink-3)]">Sent with</dt>
            <dd>{PROVIDER[m.provider] ?? m.provider}{m.provider_id ? <span className="font-mono text-[var(--tb-ink-3)]"> · {m.provider_id}</span> : null}</dd>
            <dt className="text-[var(--tb-ink-3)]">When</dt>
            <dd className="tabular-nums">{new Date(m.created_at).toLocaleString()}</dd>
            {m.links?.length ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Clicks</dt>
                <dd>{m.click_count ? `${m.click_count}, first ${new Date(m.first_clicked_at ?? m.created_at).toLocaleString()}` : "Not clicked yet"} <span className="text-[var(--tb-ink-3)]">({m.links.length} tracked link{m.links.length === 1 ? "" : "s"}: {m.links.map((l) => l.path).join(", ")})</span></dd>
              </>
            ) : null}
            {m.error ? (<><dt className="text-[var(--tb-ink-3)]">Why</dt><dd className="text-[var(--tb-critical-fg,#8e1f0b)]">{m.error}</dd></>) : null}
          </dl>
          {failed && m.channel === "email" ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-[var(--tb-line)] pt-2">
              {canResend(m) ? (
                <>
                  <Button size="small" variant="secondary" isLoading={resend.isPending} disabled={resend.isPending} onClick={() => resend.mutate()}>
                    Resend
                  </Button>
                  <span className="text-[var(--tb-ink-3)]">
                    {m.kind?.startsWith("sub_card_") ? "Sends the customer a fresh card link." : "Rebuilt from the order as it is now; customer emails go to the order's current email."}
                  </span>
                </>
              ) : (
                <span className="text-[var(--tb-ink-3)]">This email can't be rebuilt from the log, so it can't be resent from here.</span>
              )}
            </div>
          ) : null}
          {m.events.length ? (
            <ol className="mt-2 border-t border-[var(--tb-line)] pt-2">
              {m.events.map((e, i) => (
                <li key={i} className="flex gap-3 tabular-nums">
                  <span className="w-[130px] shrink-0 text-[var(--tb-ink-3)]">{when(e.at)}</span>
                  <span>{e.type === "click" ? "clicked" : e.type === "scanner_click" ? "opened by a link scanner (not counted)" : e.type === "resend_failed" ? "resend failed" : e.type}{e.link && m.links?.find((l) => l.n === e.link) ? ` · ${m.links.find((l) => l.n === e.link)!.path}` : ""}{e.reason ? ` · ${e.reason}` : ""}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      )}
    </li>
  )
}
